import { enderecoPorCoordenada, enderecoPorCoordenadas, normalizarEstado, ErroGeocodificacao } from "../../../../utils/geocodificacao";
import { tipoPlantio, tipoProPlantio } from "../../../../utils/data";
import Plantio from "../../../../models/Plantio";
import CustomHandle from "../../../../utils/CustomHandle";
import Especie from "../../../../models/Especie";
import { convertToBase64 } from "../../../../utils/conversoes";
import { ehProfissional } from "../../../../utils/papeis";
import store from "../../../../store";

// Mínimo de pontos de uma área Profissional (um polígono).
export const MIN_PONTOS_AREA = 3;

class ControllerPlantar {
  constructor(plantio, errors, especies, setShowModalPro, midiasFile, setMidiasFile, setPlantio, setErrors, setLoading, setNomeEspecie, setShowSuccess) {
    this.plantio = plantio;
    this.errors = errors;
    this.setPlantio = setPlantio;
    this.setErrors = setErrors;
    this.especies = especies;
    this.setLoading = setLoading;
    this.midiasFile = midiasFile;
    this.setNomeEspecie = setNomeEspecie;
    this.setMidiasFile = setMidiasFile;
    this.setShowSuccess = setShowSuccess;
    this.setShowModalPro = setShowModalPro;
  }

  async validarLocalizacao(e) {
    const { plantio, errors, setPlantio, setErrors } = this;
    setPlantio({ ...plantio, endereco: e, lat: null, lng: null });
    if (!plantio.lat || !plantio.lng) {
      setErrors({ ...errors, endereco: "Selecione um endereço válido" });
    } else {
      setErrors({ ...errors, endereco: "" });
    }
  }

  // `setErrors` e uma action do Redux (nao aceita funcao). Estas chamadas rodam
  // DEPOIS de uma requisicao, quando `this.errors` ja esta velho: mesclamos sobre
  // o estado mais recente da store.
  mesclarErros(parcial) {
    this.setErrors({ ...store.getState().errors, ...parcial });
  }

  // A pessoa escolheu uma sugestao da busca de endereco. O resultado ja traz
  // coordenadas, cidade e estado (utils/geocodificacao.js). Se o servico nao
  // soube dizer cidade/estado (area rural, por exemplo), o formulario mostra os
  // campos manuais em vez de deixar a pessoa sem saida.
  validarSelectLocalizacao(resultado, setForceCenter) {
    const { setPlantio } = this;
    const { lat, lng, endereco, cidade, estado } = resultado;
    setForceCenter({ lat, lng, zoom: 16 });
    setPlantio((anterior) => ({ ...anterior, endereco, cidade, estado, coordenadas: [{ lat, lng, posicao: 0 }] }));
    this.mesclarErros({
      endereco: cidade && estado ? "" : "Não identificamos a cidade e o estado deste endereço. Informe-os abaixo.",
    });
  }

  // Clique no mapa. O primeiro ponto (ou o unico, no plantio Individual) e
  // geocodificado de volta para endereco/cidade/estado; na area Profissional os
  // pontos seguintes so entram como vertices do poligono.
  //
  // O pin aparece NA HORA, antes de o servico responder, e a resposta e aplicada
  // por atualizacao funcional: o que a pessoa digitou enquanto isso nao e pisado.
  async validarClickLocalizacao(e, isPro) {
    const { plantio, setPlantio } = this;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const jaTemPonto = !!plantio.coordenadas?.[0];

    if (isPro && jaTemPonto) {
      setPlantio((anterior) => {
        const coordenadas = [...(anterior.coordenadas || [])];
        coordenadas.push({ lat, lng, posicao: coordenadas.length });
        return { ...anterior, coordenadas };
      });
      return;
    }

    setPlantio((anterior) => ({ ...anterior, coordenadas: [{ lat, lng, posicao: 0 }] }));

    try {
      const local = await enderecoPorCoordenada(lat, lng);
      const estado = normalizarEstado(local?.estado);
      const cidade = local?.cidade || "";
      setPlantio((anterior) => ({
        ...anterior,
        endereco: local?.endereco || enderecoPorCoordenadas(lat, lng),
        cidade,
        estado,
      }));
      this.mesclarErros({
        endereco: cidade && estado ? "" : "Não identificamos a cidade e o estado deste ponto. Informe-os abaixo.",
    });
    } catch (erro) {
      // Servico fora do ar ou limite atingido: o ponto continua marcado e a
      // pessoa completa estado/cidade a mao. Nao trava o formulario.
      const ocupado = erro instanceof ErroGeocodificacao && erro.tipo === "limite";
      setPlantio((anterior) => ({
        ...anterior,
        endereco: anterior.endereco || enderecoPorCoordenadas(lat, lng),
      }));
      this.mesclarErros({
        endereco: `${ocupado ? "O serviço de endereços está ocupado" : "Não foi possível obter o endereço agora"}. O ponto foi marcado; informe o estado e a cidade abaixo.`,
    });
    }
  }

  // Preenchimento manual quando a busca automatica nao identificou.
  //
  // O aviso em `errors.endereco` ("informe o estado e a cidade abaixo") PRECISA
  // sair assim que os dois campos estiverem preenchidos: o submit do formulario
  // (Validacao.emptyErrors) recusa o envio enquanto qualquer erro tiver texto, e
  // sem esta limpeza a pessoa completaria tudo e continuaria sem conseguir salvar.
  validarEstadoManual(valor) {
    this.setPlantio((anterior) => ({ ...anterior, estado: valor }));
    const completo = !!valor && !!this.plantio.cidade?.trim();
    this.mesclarErros({ estado: valor ? "" : "Selecione o estado", ...(completo ? { endereco: "" } : {}) });
  }

  validarCidadeManual(valor) {
    this.setPlantio((anterior) => ({ ...anterior, cidade: valor }));
    const completo = !!valor.trim() && !!this.plantio.estado;
    this.mesclarErros({ cidade: valor.trim() ? "" : "Informe a cidade", ...(completo ? { endereco: "" } : {}) });
  }

  // ---- Edição das pontas da área Profissional ----
  //
  // Todas usam atualização FUNCIONAL: este controller é recriado a cada render, e
  // um arraste ou um clique pode chegar com `this.plantio` já velho.
  //
  // Remove a ponta `indice` e renumera `posicao` (0, 1, 2...): o backend grava a
  // ordem dos vértices por ela, e um buraco na numeração desenharia o polígono errado.
  removerPonto(indice) {
    this.setPlantio((anterior) => {
      const coordenadas = (anterior.coordenadas || [])
        .filter((_, i) => i !== indice)
        .map((c, i) => ({ ...c, posicao: i }));
      return { ...anterior, coordenadas };
    });
  }

  desfazerUltimoPonto() {
    this.setPlantio((anterior) => ({
      ...anterior,
      coordenadas: (anterior.coordenadas || []).slice(0, -1).map((c, i) => ({ ...c, posicao: i })),
    }));
  }

  // Tira só os pontos: endereço, cidade e estado ficam (a pessoa costuma redesenhar
  // no mesmo lugar) e podem ser trocados pela busca ou por um novo clique.
  // Acrescenta uma ponta DEPOIS da ponta `depois` (o "+" no meio de um lado do
  // poligono fechado). Renumera `posicao` (0, 1, 2...), como removerPonto: o backend
  // grava a ordem pela posicao e ela nao pode ter buraco nem repetir. Inserir depois
  // da ULTIMA ponta e o lado de fechamento (ultima -> primeira): a nova vira a ultima.
  inserirPonto(depois, latLng) {
    const lat = typeof latLng?.lat === "function" ? latLng.lat() : parseFloat(latLng?.lat);
    const lng = typeof latLng?.lng === "function" ? latLng.lng() : parseFloat(latLng?.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    this.setPlantio((anterior) => {
      const lista = [...(anterior.coordenadas || [])];
      lista.splice(Math.min(depois + 1, lista.length), 0, { lat, lng, posicao: 0 });
      return { ...anterior, coordenadas: lista.map((c, i) => ({ ...c, posicao: i })) };
    });
  }

  // Mesma coisa pelo botao da lista (teclado/toque): insere no MEIO do lado que
  // sai da ponta `indice` ate a seguinte (a primeira, se for a ultima).
  inserirPontoNoMeio(indice) {
    const lista = this.plantio.coordenadas || [];
    if (lista.length < 2) return;
    const a = lista[indice];
    const b = lista[(indice + 1) % lista.length];
    this.inserirPonto(indice, {
      lat: (parseFloat(a.lat) + parseFloat(b.lat)) / 2,
      lng: (parseFloat(a.lng) + parseFloat(b.lng)) / 2,
    });
  }

  limparArea() {
    this.setPlantio((anterior) => ({ ...anterior, coordenadas: [] }));
  }

  handleOnDrag(markerIndex, latLang) {
    const coordenadas = [...this.plantio.coordenadas];
    coordenadas[markerIndex] = { ...coordenadas[markerIndex], lat: latLang.lat(), lng: latLang.lng() };
    this.setPlantio({ ...this.plantio, coordenadas })
  }

  validarEspecie(e) {
    const { errors, setErrors, especies } = this;
    if (especies) {
      const exist = especies.rows.find(
        (especie) =>
          especie?.nome_comum === e.target.value ||
          especie.nome_cientifico === e.target.value
      );
      if (exist) {
        setErrors({ ...errors, especie: "" });
        return;
      }
    }
    setErrors({ ...errors, especie: "Selecione uma espécie válida" });
  }

  validarMudas(e) {
    const { plantio, errors, setPlantio, setErrors } = this;
    setPlantio({ ...plantio, numero_mudas: e.target.value });
    if (e.target.value < 1) {
      setErrors({ ...errors, numero_mudas: "Digite um valor acima de 0" });
    } else {
      setErrors({ ...errors, numero_mudas: "" });
    }
  }

  validarOrganizacao(e) {
    const { plantio, errors, setPlantio, setErrors } = this;
    setPlantio({ ...plantio, nome_organizacao: e.target.value });
    if (e.target.value < 1) {
      setErrors({ ...errors, nome_organizacao: "Digite um nome válido" });
    } else {
      setErrors({ ...errors, nome_organizacao: "" });
    }
  }

  validarTitulo(e) {
    const { plantio, errors, setPlantio, setErrors } = this;
    setPlantio({ ...plantio, titulo: e.target.value });
    if (e.target.value < 1) {
      setErrors({ ...errors, titulo: "Digite um titulo válido" });
    } else {
      setErrors({ ...errors, titulo: "" });
    }
  }

  validarTipo(e) {
    const { plantio, errors, setPlantio, setErrors } = this;
    if (e.target.value === "Pessoal") {
      const new_plantio = { ...plantio };
      delete new_plantio.nome_organizacao;
      new_plantio.tipo = e.target.value;
      setPlantio(new_plantio);
    } else {
      setPlantio({ ...plantio, tipo: e.target.value });
    }

    if (!tipoPlantio.includes(e.target.value)) {
      setErrors({ ...errors, tipo: "Selecione um tipo de plantio válido" });
    } else {
      setErrors({ ...errors, tipo: "" });
    }
  }

  validarTipoPro(e, usuario_tipo) {
    const { plantio, setShowModalPro, errors, setPlantio, setErrors } = this;
    
    // Área é exclusiva de Profissional/Administrador. O backend também recusa
    // (403); aqui a pessoa é levada ao pedido de perfil em vez de ver o erro.
    if (e.target.value === "Profissional" && !ehProfissional({ tipo: usuario_tipo })) {
      setShowModalPro(true);
      setPlantio({ ...plantio, tipo_categoria: '' });
    } else {
      setPlantio({ ...plantio, tipo_categoria: e.target.value });
    }


    if (!tipoProPlantio.includes(e.target.value)) {
      setErrors({ ...errors, tipo: "Selecione um tipo de plantio válido" });
    } else {
      setErrors({ ...errors, tipo: "" });
    }
  }

  validarMidias(midias, midiasFile, limit = 5) {
    let totalLength = midias.length + midiasFile.length;
    if (totalLength <= limit) {
      return true;
    }
    return false;
  }

  async findAllEspecies(setEspecies) {
    this.setLoading(true);
    const result = await Especie.findAll();
    const onSuccess = (response) => {
      setEspecies(response);
    }

    CustomHandle.handleResult(result, this.errors, this.setErrors, onSuccess);

    this.setLoading(false);
  }

  getLoacation(setMapCenter) {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.log("Erro ao obter a posição do usuário:", error);
        }
      );
    }
  }

  async findPlantio(plantio_id) {
    if (plantio_id) {
      document.getElementById('midias').value = null;
      this.setMidiasFile([]);
      this.setLoading(true);

      const result = await Plantio.findById(plantio_id);

      const onSuccess = (response) => {

        this.setNomeEspecie(response.tipo_categoria === "Profissional" ?
          `${response.especies.length} especies selecionadas` :
          `${response.especies[0].especie.nome_cientifico} - ${response.especies[0]?.especie?.nome_comum}`
        );
        this.setPlantio({ ...response});
      };

      CustomHandle.handleResult(result, this.errors, this.setErrors, onSuccess);

      this.setLoading(false);
    } else {
      this.limparCampos();
    }
  }

  async filtrarEspecies(e, setEspecies) {
    const searchValue = e.target.value;
    this.setNomeEspecie(searchValue);
    let result = [];

    const onSuccess = (response) => {
      setEspecies(response);
      this.validarEspecie(e, this.errors, this.setErrors, response.rows);
    }

    if (searchValue) {
      result = await Especie.findSearch(searchValue);
    } else {
      result = await Especie.findAll();
    }

    CustomHandle.handleResult(result, this.errors, this.setErrors, onSuccess);


  }

  handleDeleteMidia(midia, setMidiaDelete) {
    if (this.plantio.midias.length + this.midiasFile.length <= 1) {
      this.setErrors({ ...this.errors, midias: "Não é possível excluir. O plantio precisa ter pelo menos uma mídia." });
    } else {
      setMidiaDelete(midia);
    }
  }

  async handleRemoveMidia(midiaDelete, setMidiaDelete) {
    let newMidias = [];

    if (!midiaDelete.id) {
      newMidias = this.midiasFile.filter((n_file) => midiaDelete !== n_file);
      this.setMidiasFile(newMidias);
    } else {
      const result = await Plantio.deleteMidia(this.plantio.id, midiaDelete.id);
      const onSuccess = () => {
        newMidias = this.plantio.midias.filter((n_file) => midiaDelete !== n_file);
        this.setPlantio((prevPlantio) => ({ ...prevPlantio, midias: newMidias }));
      };

      CustomHandle.handleResult(result, this.errors, this.setErrors, onSuccess);

    }

    setMidiaDelete(null);

    if (!this.errors.midias) {
      if (!this.validarMidias(this.plantio.midias, newMidias)) {
        this.setErrors({ ...this.errors, midias: 'Selecione no máximo 5 midias' });
      } else {
        this.setErrors({ ...this.errors, midias: '' });
      }
    }

    if (newMidias.length === 0) {
      document.getElementById('midias').value = null;
    }
  }

  handleChangeMidia(e) {
    const { files: selectedFiles } = e.target;
    const updatedMidias = [...this.midiasFile, ...selectedFiles];

    const isMidiasValid = this.validarMidias(this.plantio.midias, updatedMidias);
    const errorMessage = isMidiasValid ? '' : 'Selecione no máximo 5 midias';

    this.setErrors({ ...this.errors, midias: errorMessage });

    if (isMidiasValid) {
      this.setMidiasFile(updatedMidias);
    }
  }

  handleAddCountMudas(index) {
    const especies = [...this.plantio.especies];
    especies[index].numero_mudas += 1;
    this.setPlantio({ ...this.plantio, especies });
  }

  handleRemoveCountMudas(index) {
    const especies = [...this.plantio.especies];
    if (especies[index].numero_mudas > 1) {
      especies[index].numero_mudas -= 1;
      this.setPlantio({ ...this.plantio, especies });
    }
  }

  handleChangeCountMudas(e, index) {
    const especies = [...this.plantio.especies];
    especies[index].numero_mudas = e.target.value > 1 ? e.target.value : 1;
    this.setPlantio({ ...this.plantio, especies });

  }

  onClickSelectEspecie(newEspecie) {
    const { nome_comum, nome_cientifico, id } = newEspecie;
    let especies = [...this.plantio.especies];
    let count = especies.length;

    if (especies.find(especie => especie.especie_id === id)) {
      count -= 1;
      especies = especies.filter(especie => especie.especie_id !== id);
    } else {
      count += 1;
      especies.push({ especie_id: id, numero_mudas: 1, especie: { nome_cientifico, nome_comum } });
    }

    this.setNomeEspecie(
      this.plantio.tipo_categoria === 'Profissional' ?
        `${count} especie(s) selecionadas` :
        `${nome_comum} - ${nome_cientifico}`
    );

    this.setPlantio((prevPlantio) => ({ ...prevPlantio, especies: this.plantio.tipo_categoria === 'Profissional' ? especies : [{ especie_id: id, numero_mudas: 1, especie: { nome_cientifico, nome_comum }  }] }));
    this.setErrors({ ...this.errors, especies: '' });
  }

  tratarPlantio(plantio) {
    let new_plantio = { ...plantio };

    if (new_plantio.tipo === 'Pessoal') {
      delete new_plantio.nome_organizacao;
    }

    if (!new_plantio.data) {
      new_plantio = { ...new_plantio, data: new Date() };
    }

    return new_plantio;
  }

  async addMidiasOnEdit(base64Array, setAtualizarPlantio, atualizarPlantio) {
    const result = await Plantio.addMidias({ plantio_id: this.plantio.id, midias: base64Array });
    const onSuccess = () => {
      setAtualizarPlantio(!atualizarPlantio);
      this.setShowSuccess(true);
    };
    CustomHandle.handleResult(result, this.errors, this.setErrors, onSuccess);
  }

  async handleSubmit(e, usuario_id, setAtualizarPlantio, atualizarPlantio, edit = false) {
    e.preventDefault();

    // Uma área é um polígono: precisa de pelo menos 3 pontos. Sem isto o formulário
    // aceitava 1 ou 2 pontos (uma "área" que é um ponto ou uma linha).
    const pontos = this.plantio.coordenadas?.length || 0;
    if (this.plantio.tipo_categoria === 'Profissional' && pontos < MIN_PONTOS_AREA) {
      this.setErrors({ ...this.errors, geral: `Uma área precisa de pelo menos ${MIN_PONTOS_AREA} pontos no mapa (hoje tem ${pontos}).` });
      return;
    }

    convertToBase64(this.midiasFile)
      .then(async (base64Array) => {
        const new_plantio = this.tratarPlantio(this.plantio);
        const submitAction = async () => {
          const resultado = await Plantio.create({ ...new_plantio, usuario_id, midias: base64Array });
          // 403 = o servidor recusou área para quem não é Profissional (a tela
          // estava com o papel desatualizado, ou o pedido foi forjado). A
          // mensagem do backend segue para errors.geral; o modal leva ao pedido.
          if (resultado?.response?.status === 403) this.setShowModalPro(true);
          return resultado;
        };
        const onSuccess = edit ?
          async () => await this.addMidiasOnEdit(base64Array, setAtualizarPlantio, atualizarPlantio) :
          () => this.setShowSuccess(true);

        await CustomHandle.submitForm('form-plantar', submitAction, this.errors, this.setErrors, this.setLoading, onSuccess);
      })
      .catch(() => {
        this.setErrors({ ...this.errors, midias: "Ocorreu um erro ao tratar as imagens. Tente novamente" })
      });
  }

  limparCampos() {
    this.setPlantio({ ...Plantio.model });
    this.setNomeEspecie('');
    this.setMidiasFile([]);
    this.setShowSuccess(false);
    document.getElementById('midias').value = null;
  }

}

export default ControllerPlantar;
